<template>
  <div>
    <dialog-confirm
      v-if="context === 'update'"
      ref="update-title-dialog"
      :confirm-text="$t('yes')"
      :reject-text="$t('no')">
      <template #title>
        {{ $t('reports_ui.update_report_title_query') }}
      </template>
      <div class="text-subtitle-1">
        {{ $t('reports_ui.report_parameters_title_change_text')}}
      </div>
      <h5 class="mt-2">
        {{ defaultReportName() }}
      </h5>
    </dialog-confirm>
    <v-form>
      <v-text-field
        v-if="context !== 'create'"
        v-model="localReport.name"
        :label="$tc('name')"
        :error-messages="nameErrors"
        prepend-inner-icon="mdi-form-textbox"/>
      <vf-autocomplete
        v-model="reportCustomers"
        :items="availableCustomers"
        :error-messages="customerErrors"
        :label="$tc('customer', 2)"/>
      <div class="d-flex align-center">
        <date-picker
          v-model="startDate"
          :timezone="timezone"
          :output-moment="true"
          :label="$t('start_date')"
          prepend-inner-icon="mdi-calendar"
          :error-messages="dateRangeErrors"
          :reset-on-timezone-change="context === 'create'"/>
        <span class="text-subtitle-1 mx-2">{{ $t('to') }}</span>
        <date-picker
          v-model="endDate"
          :timezone="timezone"
          :output-moment="true"
          :end-of-day="true"
          :label="$t('end_date')"
          prepend-inner-icon="mdi-calendar"
          :error="dateRangeErrors.length > 0"
          :reset-on-timezone-change="context === 'create'"/>
      </div>
      <date-selector-shortcuts :lowerBound.sync="startDate" :upperBound.sync="endDate" :timezone="timezone" :show-title="false"/>
      <vf-autocomplete
        v-model="selectedViolationTypes"
        :items="violationTypeOptions"
        :label="$tc('violation_type', 2)"/>
      <vf-autocomplete
        v-model="selectedZones"
        :items="zoneOptions"
        :label="$tc('zone', 2)"/>
      <vf-autocomplete
        v-model="selectedForecastPhases"
        :items="forecastPhaseOptions"
        :label="$tc('forecast_phase', 2)"/>
      <v-select
        v-model="forecastingEnabled"
        :items="forecastingEnabledOptions"
        :label="$tc('forecasting_status', 1)"
        clearable/>
    </v-form>
  </div>
</template>

<script>
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import DatePicker from '@/components/shared/DatePicker'
import DateSelectorShortcuts from '@/components/report/DateSelectorShortcuts'
import DialogConfirm from '../shared/DialogConfirm'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import ReportFormMixin from '@/components/report/ReportFormMixin'
import ReportUtils from '@/components/report/ReportUtils'
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import ProductRecord from '@/orm/models/ProductRecord'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { get, map } from 'lodash'
import Moment from 'moment-timezone'

export default {
  name: 'ForecastViolationValidationReportForm',
  components: {
    VfAutocomplete,
    DatePicker,
    DateSelectorShortcuts,
    DialogConfirm
  },
  mixins: [
    CurrentUserMixin,
    ApiUtilitiesMixin,
    ValidationMixin,
    ReportFormMixin
  ],
  validations: {
    localReport: {
      name: {
        required: requiredIf(function () {
          return this.action !== 'create'
        })
      }
    },
    reportCustomers: {
      required
    },
    dateRange: {
      rangeIsValid: function () {
        return ReportUtils.dateRangeIsValid(this.startDate, this.endDate)
      }
    }
  },
  data () {
    return {
      reportType: 'ForecastViolationValidationReport',
      reportCustomers: [],
      startDate: null,
      endDate: null,
      selectedViolationTypes: [],
      selectedZones: [],
      selectedForecastPhases: [],
      forecastingEnabled: null
    }
  },
  computed: {
    violationTypeOptions () {
      return [
        { text: this.$t('fatal_violation:cold'), value: RouteViolationRecord.TYPE_COLD },
        { text: this.$t('fatal_violation:hot'), value: RouteViolationRecord.TYPE_HOT }
      ]
    },
    zoneOptions () {
      return [
        { text: this.$t('zone:freezer'), value: ProductRecord.ZONE_FREEZER },
        { text: this.$t('zone:cooler'), value: ProductRecord.ZONE_COOLER },
        { text: this.$t('zone:dry'), value: ProductRecord.ZONE_DRY },
        { text: this.$t('zone:cab'), value: ProductRecord.ZONE_CAB }
      ]
    },
    forecastPhaseOptions () {
      return [
        { text: this.$t('forecast_phase:staging'), value: 'staging' },
        { text: this.$t('forecast_phase:full_route'), value: 'full_route' }
      ]
    },
    forecastingEnabledOptions () {
      return [
        { text: this.$t('forecasting_enabled'), value: true },
        { text: this.$t('forecasting_disabled'), value: false }
      ]
    },
    availableCustomers () {
      return map(this.customers, customer => {
        return {
          text: customer.name,
          value: customer.id
        }
      })
    },
    nameErrors () {
      return this.validationErrors([{
        prop: this.$v.localReport.name,
        message: this.$t('required')
      }])
    },
    customerErrors () {
      return this.validationErrors([{
        prop: this.$v.reportCustomers,
        message: this.$t('required')
      }])
    },
    dateRangeErrors () {
      return this.validationErrors([{
        prop: this.$v.dateRange,
        message: this.$t('invalid_date_range')
      }])
    }
  },
  methods: {
    /** Hook to populate report (called from mixin) */
    populateReportForm () {
      this.preSelectCustomers()
      this.preSelectDates()
      this.preSelectFilters()
    },
    /**
     * Override mixin method setLocalParameters
     * @param reportParameters
     * @returns {Promise<void>}
     */
    async setLocalReportParameters (reportParameters) {
      if (this.action === 'create' || this.action === 'duplicate') {
        this.localReport = this.$orm('Report').createRecord({
          type: this.reportType,
          name: this.localReport.name || this.defaultReportName(),
          parameters: reportParameters
        })
      } else {
        const datesChanged = reportParameters.startDate !== get(this.report, 'parameters.startDate') ||
                            reportParameters.endDate !== get(this.report, 'parameters.endDate')
        if (datesChanged && await this.$refs['update-title-dialog'].confirm()) {
          this.localReport.name = this.defaultReportName()
        }
        this.localReport.parameters = reportParameters
      }
    },
    /**
     * Implement ReportFormMixin method buildReportParameters
     * @returns {{}}
     */
    buildReportParameters () {
      const reportParameters = {}
      reportParameters.startDate = this.startDate.format()
      reportParameters.endDate = this.endDate.format()
      reportParameters.customerIds = this.reportCustomers

      // Optional filters
      if (this.selectedViolationTypes && this.selectedViolationTypes.length > 0) {
        reportParameters.violationTypes = this.selectedViolationTypes
      }
      if (this.selectedZones && this.selectedZones.length > 0) {
        reportParameters.zones = this.selectedZones
      }
      if (this.selectedForecastPhases && this.selectedForecastPhases.length > 0) {
        reportParameters.forecastPhases = this.selectedForecastPhases
      }
      if (this.forecastingEnabled !== null) {
        reportParameters.forecastingEnabled = this.forecastingEnabled
      }

      return reportParameters
    },
    /**
     * Implement ReportFormMixin method defaultReportName
     * @returns {string}
     */
    defaultReportName () {
      return `${this.$t('reports_ui.forecast_violation_validation_report.name')} (${this.startDate.format('L')} - ${this.endDate.format('L')})`
    },
    /**
     * Pre-select dates from existing report or default to last 30 days
     */
    preSelectDates () {
      const preSelectedStartDate = get(this, 'report.parameters.startDate')
      const preSelectedEndDate = get(this, 'report.parameters.endDate')

      if (preSelectedStartDate && preSelectedEndDate) {
        this.startDate = Moment.tz(preSelectedStartDate, this.timezone)
        this.endDate = Moment.tz(preSelectedEndDate, this.timezone)
      } else {
        // Default to last 30 days
        this.endDate = Moment().tz(this.timezone).startOf('day')
        this.startDate = Moment().tz(this.timezone).subtract(30, 'days').startOf('day')
      }
    },
    /**
     * Pre-select filters from existing report
     */
    preSelectFilters () {
      this.selectedViolationTypes = get(this, 'report.parameters.violationTypes', [])
      this.selectedZones = get(this, 'report.parameters.zones', [])
      this.selectedForecastPhases = get(this, 'report.parameters.forecastPhases', [])
      this.forecastingEnabled = get(this, 'report.parameters.forecastingEnabled', null)
    }
  }
}
</script>

<style scoped>
</style>
